import React, { useReducer, useRef } from 'react';
import PropTypes, { arrayOf, shape } from 'prop-types';
import { Input } from 'reactstrap';
import uuid from 'uuid';

import TableWidgetModal from 'app/TableWidgetModal/components/TableWidgetModal';
import { PrimaryButton } from 'ui/Components';
import isEqual from 'lodash/isEqual';

import { getDataFromExcelSheets, getFinalSheet, getWorkBookFromExcel } from 'utils/excelUtils';
import { reducer } from './reducer';
import {
  SET_CURRENT_METRIC,
  SET_METRIC_RADIO_STATE,
  SET_METRICS_SYSTEM,
  SET_TABLE_COLS,
  SET_TABLE_MODE,
  SET_TABLE_ROWS,
  UPDATE_MODAL_STATE,
  UPDATE_TABLE_DATA
} from './actions';
import { DEFAULT_SHEET_NAME, INITIAL_STATE } from './const';
import HelpIcon from '../HelpIcon';
import Dropdown from '../Dropdown';
import { ALLOWED_COLUMNS_COUNT, ALLOWED_ROWS_COUNT, VERTICAL_MODE } from '../../const';

/**
 * component which render row and column input
 * @param {Array} newCols - data from parents wrapper
 * @param {Array} newRows - data from parents wrapper
 * @param {Function} handleChangeCol - function for changing columns
 * @param {Function} handleChangeRow - function for changing rows
 * @returns {React.Component} - node which wrapped rows and input
 */
const renderRowAndColumnInput = (newCols, newRows, handleChangeCol, handleChangeRow) => (
  <div className="d-flex justify-content-between">
    <div className="d-flex flex-column mr-2">
      <span className="font-weight-bold">Rows</span>
      <Input type="number" name="newRows" value={newRows} onChange={handleChangeRow} />
    </div>
    <div className="d-flex flex-column mr-2 ml-2">
      <span className="font-weight-bold">Columns</span>
      <Input type="number" name="newCols" value={newCols} onChange={handleChangeCol} />
    </div>
  </div>
);

/**
 * This component can show widget with some users content.
 * @param {object} props - props with fieldContract
 * @returns {React.Component} a react component with some data
 */
const TableWidget = props => {
  const {
    fieldContract: { name, required, helpText },
    selectedValue
  } = props;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { data, isOpen, newRows, newCols, mode, metricsSystems, currentMetric } = state;
  const excelFileInput = useRef(null);

  /**
   * create new table and set data into new table
   */
  const handleCreateNewTable = () => {
    const rows = [];
    for (let i = 0; i < newRows; i++) {
      rows.push({
        rowData: new Array(newCols).fill({ [DEFAULT_SHEET_NAME]: '' }),
        rowOptions: {}
      });
    }
    dispatch({ type: SET_METRICS_SYSTEM, metricsSystems: [DEFAULT_SHEET_NAME] });
    dispatch({ type: SET_CURRENT_METRIC, currentMetric: DEFAULT_SHEET_NAME });
    dispatch({ type: UPDATE_TABLE_DATA, data: rows });
    dispatch({ type: UPDATE_MODAL_STATE, isOpen: true });
  };

  const handleGetSavedTable = () => {
    if (!data.length) {
      // eslint-disable-next-line react/prop-types
      const savedRows = props.selectedValue.rows || [];
      const metrics = [];
      const getMetrics = () => {
        savedRows.forEach(item => {
          item.rowData.forEach(column => {
            const keys = Object.keys(column);
            keys.forEach(key => {
              if (!metrics.includes(key)) {
                metrics.push(key);
              }
            });
          });
        });
      };

      getMetrics();

      dispatch({ type: SET_METRICS_SYSTEM, metricsSystems: metrics });
      dispatch({ type: SET_CURRENT_METRIC, currentMetric: metrics[0] });
      dispatch({ type: UPDATE_TABLE_DATA, data: savedRows });
    } else if (selectedValue.rows && !isEqual(data, selectedValue.rows)) {
      dispatch({ type: UPDATE_TABLE_DATA, data: selectedValue.rows });
    } else if (data.length) {
      dispatch({ type: UPDATE_TABLE_DATA, data });
    }

    dispatch({ type: UPDATE_MODAL_STATE, isOpen: true });
  };

  /**
   * function that delete sheet, update table data
   * @param {string} sheet - name of sheet, that will be delete
   */
  const deleteSheet = sheet => {
    const updatedData = data.map(row => ({
      rowOptions: row.rowOptions,
      rowData: row.rowData.map(columnItem => {
        const { sheet, ...finalData } = columnItem;
        return finalData;
      })
    }));
    const updatedMetricsSystems = metricsSystems.filter(item => item !== sheet);
    if (currentMetric === sheet) {
      dispatch({ type: SET_CURRENT_METRIC, currentMetric: updatedMetricsSystems[0] });
    }
    dispatch({
      type: SET_METRICS_SYSTEM,
      metricsSystems: updatedMetricsSystems
    });
    dispatch({ type: UPDATE_TABLE_DATA, data: updatedData });
  };

  /**
   * function that add new sheet and update table data
   * @param {string} newName - name for new sheet
   */
  const addNewSheet = newName => {
    const updatedData = data.map(row => ({
      rowOptions: row.rowOptions,
      rowData: row.rowData.map(columnItem => ({ ...columnItem, ...{ newName: '' } }))
    }));
    dispatch({ type: SET_METRICS_SYSTEM, metricsSystems: [...metricsSystems, newName] });
    dispatch({ type: UPDATE_TABLE_DATA, data: updatedData });
  };

  /**
   * function that change sheet name
   * @param {string} sheet - old name of sheet
   * @param {string} newName - new name of sheet
   * @param {Array} tableData - table data
   */
  const renameSheet = (sheet, newName, tableData) => {
    const renameProp = (oldProp, newProp, { [oldProp]: old, ...others }) => ({
      [newProp]: old,
      ...others
    });

    const updatedData = tableData.map(row => ({
      rowOptions: row.rowOptions,
      rowData: row.rowData.map(columnItem => renameProp(sheet, newName, columnItem))
    }));

    /**
     * function that return updated array with sheets
     * @returns {*[]}
     */
    const getUpdatedMetricSystems = () => {
      const updatedMetrics = [...metricsSystems];
      updatedMetrics[metricsSystems.findIndex(item => item === sheet)] = newName;
      return updatedMetrics;
    };

    dispatch({ type: SET_METRICS_SYSTEM, metricsSystems: getUpdatedMetricSystems() });
    dispatch({ type: SET_METRIC_RADIO_STATE, metricRadioState: newName });
    dispatch({ type: UPDATE_TABLE_DATA, data: updatedData });
  };

  /**
   * function that create table from data
   * @param {Array} rowData - data from which a new table will be created
   */
  const handleCreateFromExcel = rowData => {
    const rows = [];
    for (let i = 0; i < rowData.length; i++) {
      rows.push({
        rowData: rowData[i].map(item => item),
        rowOptions: {}
      });
    }

    dispatch({ type: SET_TABLE_MODE, mode: VERTICAL_MODE });
    dispatch({ type: UPDATE_TABLE_DATA, data: rows });
    dispatch({ type: UPDATE_MODAL_STATE, isOpen: true });
  };

  /**
   * function for rendering block for creating table
   * @returns {React.Component} - button, which create new Table
   */
  const renderNewTableBlock = () => {
    /**
     * function for change Rows select value
     * @param {string} value - the event to be handled
     */
    const onChangeRows = value => {
      dispatch({ type: SET_TABLE_ROWS, newRows: parseInt(value, 10) });
    };

    /**
     * function for change Cols select value
     * @param {string} value - the event to be handled
     */
    const onChangeColumns = value => {
      dispatch({ type: SET_TABLE_COLS, newCols: parseInt(value, 10) });
    };

    const columnsData = {
      allowedValues: [...Array(ALLOWED_ROWS_COUNT).keys()],
      name: 'Count of columns'
    };
    const rowsData = {
      allowedValues: [...Array(ALLOWED_COLUMNS_COUNT).keys()],
      name: 'Count of rows'
    };

    /**
     * function that triggered click on fileInput for the choosing excel file
     * @returns {*}
     */
    const triggerFileInputClick = () => excelFileInput.current.click();

    /**
     * function that converted file to some state data:
     * - data - data for building new table
     * - metricRadioState - setting default state for switcher
     * - currentMetric - set switchers state for first render
     * @param {File} selectedFiles - file from fileInput
     */
    const convertingData = selectedFiles => {
      if (selectedFiles) {
        const fileReader = new FileReader();
        fileReader.onload = event => {
          const excelData = event.target.result;
          const workbook = getWorkBookFromExcel(excelData, { type: 'binary', raw: true });
          const sheetNames = workbook.SheetNames;
          const arrayOfSheets = getDataFromExcelSheets(sheetNames, workbook);
          const finalSheet = getFinalSheet(arrayOfSheets);
          dispatch({
            type: SET_METRICS_SYSTEM,
            metricsSystems: [DEFAULT_SHEET_NAME]
          });
          dispatch({ type: SET_CURRENT_METRIC, currentMetric: DEFAULT_SHEET_NAME });
          dispatch({ type: SET_METRIC_RADIO_STATE, metricRadioState: DEFAULT_SHEET_NAME });
          dispatch({ type: UPDATE_TABLE_DATA, data: finalSheet });
          handleCreateFromExcel(finalSheet);
        };
        fileReader.readAsBinaryString(selectedFiles);
      }
    };

    /**
     * function that calls a function to convert a file into data by passing a file to it
     * @param {Event} event - the event to be handled
     */
    const triggerUpload = event => {
      convertingData(event.currentTarget.files[0]);
    };

    return (
      <React.Fragment>
        <Dropdown
          updateLocation="columnSelect"
          onChange={onChangeColumns}
          selectedValue={newCols}
          fieldContract={columnsData}
        />
        <Dropdown
          updateLocation="rowsSelect"
          onChange={onChangeRows}
          selectedValue={newRows}
          fieldContract={rowsData}
        />
        <PrimaryButton className="mt-3" onClick={handleCreateNewTable}>
          Create New Table
        </PrimaryButton>

        <input
          ref={excelFileInput}
          className="d-none"
          type="file"
          id="fileUpload"
          onChange={event => triggerUpload(event)}
          accept=".xls,.xlsx"
        />
        <div className="d-flex">
          <PrimaryButton
            className="mt-3"
            onClick={triggerFileInputClick}
            type="button"
            id="uploadExcel"
          >
            Upload from Excel
          </PrimaryButton>
        </div>
      </React.Fragment>
    );
  };

  /**
   * function which changing tables mode
   * @param {object} event - the event to be handled
   */
  const handleChangeMode = event => {
    dispatch({ type: SET_TABLE_MODE, mode: event.target.value });
  };

  const isEdit = data.length || (props.selectedValue.rows && props.selectedValue.rows.length);

  /**
   * set new rows from events value
   * @param {object} event - the event to be handled
   */
  const handleChangeRow = event => {
    dispatch({ type: SET_TABLE_ROWS, newRows: event.target.value });
  };

  /**
   * set new cols from events value
   * @param {object} event - the event to be handled
   */
  const handleChangeCol = event => {
    dispatch({ type: SET_TABLE_COLS, newCols: event.target.value });
  };

  /**
   * function for rendering edit functionality
   * @returns {React.Component} - return button, which open modal
   */
  const renderEditTableButton = () => (
    <PrimaryButton onClick={handleGetSavedTable}>Edit Table</PrimaryButton>
  );

  return (
    <div className="d-flex flex-column my-3">
      <span className="d-flex align-items-center font-weight-bold mb-2">
        {`${name}${required ? '*' : ''}`}
        {helpText && <HelpIcon message={helpText} id={uuid()} />}
      </span>

      <span className="d-flex align-items-center font-weight-bold mb-2">
        {!data && renderRowAndColumnInput(newCols, newRows, handleChangeCol, handleChangeRow)}
      </span>

      <span className="d-flex align-items-center font-weight-bold mb-2">
        <div className="d-inline-block">
          {isEdit ? renderEditTableButton() : renderNewTableBlock()}
        </div>
      </span>

      <TableWidgetModal
        closeModal={() => dispatch({ type: UPDATE_MODAL_STATE, isOpen: false })}
        data={data}
        isOpen={isOpen}
        mode={mode}
        newRows={newRows}
        metricsSystems={metricsSystems}
        handleChangeMode={handleChangeMode}
        currentMetric={currentMetric}
        renameSheet={renameSheet}
        deleteSheet={deleteSheet}
        addNewSheet={addNewSheet}
        {...props}
      />
    </div>
  );
};

TableWidget.propTypes = {
  fieldContract: PropTypes.shape({
    name: PropTypes.string,
    required: PropTypes.bool,
    helpText: PropTypes.string
  }).isRequired,
  selectedValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      shape({
        rowData: arrayOf(PropTypes.object)
      })
    )
  ]).isRequired
};

export default TableWidget;
